import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ─── SQL Parser robuste ──────────────────────────────────────────

interface ParsedAliment {
  nom: string;
  categorie: string;
  image: string | null;
  description: string;
  glucides_pour_100g: number;
  calories_100g: number;
  fibres_100g: number;
  index_glycemique: number;
  nutriscore: string | null;
  proteines_100g: null;
  lipides_100g: null;
  sucres_100g: null;
  charge_glycemique: null;
  aliases: string[];
  source: string;
  score_confiance: number;
  created_at: string;
}

function parseSqlValues(sql: string): ParsedAliment[] {
  const results: ParsedAliment[] = [];
  const insertRegex = /INSERT\s+INTO\s+`?aliments`?\s*\(([^)]+)\)\s*VALUES\s*\(([^)]+)\)\s*;/gi;

  let match: RegExpExecArray | null;
  while ((match = insertRegex.exec(sql)) !== null) {
    const columns = match[1]
      .split(',')
      .map((c) => c.trim().replace(/^`/, '').replace(/`$/, ''));
    const valuesRaw = match[2];

    const values = parseValuesList(valuesRaw);
    const obj: Record<string, string | null> = {};

    columns.forEach((col, i) => {
      obj[col] = values[i] ?? null;
    });

    const nom = obj.nom ?? '';
    if (!nom) continue;

    results.push({
      nom,
      categorie: (obj.categorie ?? '').toLowerCase().replace(/\s+/g, '_'),
      image: obj.image || null,
      description: obj.description ?? '',
      glucides_pour_100g: parseFloat(obj.glucides_pour_100g ?? '0') || 0,
      calories_100g: parseInt(obj.calories_100g ?? '0', 10) || 0,
      fibres_100g: parseFloat(obj.fibres_pour_100g ?? obj.fibres_100g ?? '0') || 0,
      index_glycemique: parseInt(obj.index_glycemique ?? '0', 10) || 0,
      nutriscore: obj.nutriscore || null,
      proteines_100g: null,
      lipides_100g: null,
      sucres_100g: null,
      charge_glycemique: null,
      aliases: [],
      source: 'Import CSV/SQL',
      score_confiance: 3,
      created_at: obj.created_at ?? new Date().toISOString(),
    });
  }

  return results;
}

/** Parse une liste SQL de valeurs comme 'a','b',NULL,123,'d''e' */
function parseValuesList(raw: string): (string | null)[] {
  const values: (string | null)[] = [];
  let current = '';
  let inString = false;
  let i = 0;

  while (i < raw.length) {
    const ch = raw[i];
    const next = raw[i + 1];

    if (!inString) {
      if (ch === "'") {
        inString = true;
        current = '';
      } else if (/[\s,]/.test(ch)) {
        // skip whitespace and commas outside strings
      } else if (raw.substring(i, i + 4).toUpperCase() === 'NULL') {
        values.push(null);
        i += 3; // skip 'NULL'
      } else {
        // unquoted number or token
        let token = '';
        while (i < raw.length && !/[\s,]/.test(raw[i]) && raw[i] !== "'") {
          token += raw[i];
          i++;
        }
        values.push(token.trim());
        i--; // because the loop will i++
      }
    } else {
      if (ch === "'" && next === "'") {
        current += "'";
        i++; // skip escaped quote
      } else if (ch === "'") {
        inString = false;
        values.push(current);
      } else {
        current += ch;
      }
    }
    i++;
  }

  return values;
}

// ─── Composant ─────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);
const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
);
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);
const LoaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
);
const DatabaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
);

interface ImportResult {
  totalParsed: number;
  inserted: number;
  duplicates: number;
  errors: number;
  details: string[];
}

export default function ImportAlimentsSQL() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ParsedAliment[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseSqlValues(text);
      setPreview(parsed.slice(0, 50)); // preview first 50
      setStep('preview');
    };
    reader.readAsText(f);
  }, []);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);

    try {
      const reader = new FileReader();
      const text = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.readAsText(file);
      });

      const allParsed = parseSqlValues(text);
      if (allParsed.length === 0) {
        setResult({
          totalParsed: 0,
          inserted: 0,
          duplicates: 0,
          errors: 1,
          details: ['Aucun INSERT INTO aliments trouvé dans le fichier.'],
        });
        setLoading(false);
        return;
      }

      // 1. Fetch existing names to check duplicates
      const { data: existing, error: fetchErr } = await supabase
        .from('aliments')
        .select('nom');

      if (fetchErr) {
        setResult({
          totalParsed: allParsed.length,
          inserted: 0,
          duplicates: 0,
          errors: 1,
          details: [`Erreur lecture base: ${fetchErr.message}`],
        });
        setLoading(false);
        return;
      }

      const existingNames = new Set((existing ?? []).map((e: { nom: string }) => e.nom.toLowerCase().trim()));

      // 2. Filter duplicates
      const newAliments = allParsed.filter((a) => !existingNames.has(a.nom.toLowerCase().trim()));
      const duplicates = allParsed.length - newAliments.length;

      if (newAliments.length === 0) {
        setResult({
          totalParsed: allParsed.length,
          inserted: 0,
          duplicates,
          errors: 0,
          details: [`${duplicates} aliment(s) déjà présent(s) en base. Aucun nouvel aliment à insérer.`],
        });
        setLoading(false);
        return;
      }

      // 3. Batch insert (Supabase recommends max 1000 per batch)
      const BATCH_SIZE = 500;
      let inserted = 0;
      let errors = 0;
      const details: string[] = [];

      for (let i = 0; i < newAliments.length; i += BATCH_SIZE) {
        const batch = newAliments.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('aliments').insert(batch);
        if (error) {
          errors += batch.length;
          details.push(`Erreur batch ${i / BATCH_SIZE + 1}: ${error.message}`);
        } else {
          inserted += batch.length;
        }
      }

      setResult({
        totalParsed: allParsed.length,
        inserted,
        duplicates,
        errors,
        details: errors === 0
          ? [`${inserted} aliment(s) inséré(s) avec succès. ${duplicates} doublon(s) ignoré(s).`]
          : [`${inserted} inséré(s), ${duplicates} doublon(s), ${errors} erreur(s).`, ...details],
      });
      setStep('done');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setResult({
        totalParsed: 0,
        inserted: 0,
        duplicates: 0,
        errors: 1,
        details: [`Erreur pendant l'import: ${msg}`],
      });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview([]);
    setResult(null);
    setStep('upload');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 text-emerald-600 rounded-xl">
          <DatabaseIcon />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Importer des aliments (fichier SQL)</h3>
          <p className="text-gray-500 text-xs">Parse un fichier .sql contenant des INSERT INTO aliments et les injecte dans Supabase</p>
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-teal-300 hover:bg-teal-50/30 transition-colors cursor-pointer"
        >
          <div className="w-12 h-12 mx-auto flex items-center justify-center bg-gray-50 rounded-full text-gray-400 mb-3">
            <UploadIcon />
          </div>
          <p className="text-sm font-medium text-gray-700">Cliquez pour sélectionner un fichier .sql ou .txt</p>
          <p className="text-xs text-gray-400 mt-1">Extraire le ZIP, puis choisir le fichier SQL</p>
          <input
            ref={fileRef}
            type="file"
            accept=".sql,.txt"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && file && (
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
            <FileIcon />
            <span className="font-medium">{file.name}</span>
            <span className="text-gray-400">— {preview.length}{preview.length >= 50 ? '+' : ''} aliments détectés</span>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 mb-4 max-h-64 overflow-y-auto text-xs">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-2">#</th>
                  <th className="py-1 pr-2">Nom</th>
                  <th className="py-1 pr-2">Catégorie</th>
                  <th className="py-1 pr-2">G/100g</th>
                  <th className="py-1 pr-2">Kcal</th>
                  <th className="py-1">IG</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((a, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0">
                    <td className="py-1 pr-2 text-gray-400">{i + 1}</td>
                    <td className="py-1 pr-2 font-medium text-gray-800 truncate max-w-[150px]">{a.nom}</td>
                    <td className="py-1 pr-2 text-gray-500">{a.categorie}</td>
                    <td className="py-1 pr-2">{a.glucides_pour_100g}</td>
                    <td className="py-1 pr-2">{a.calories_100g}</td>
                    <td className="py-1">{a.index_glycemique || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.length >= 50 && (
              <p className="text-center text-gray-400 mt-2 italic">... et plus encore</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              {loading ? <LoaderIcon /> : <DatabaseIcon />}
              {loading ? 'Import en cours...' : 'Lancer l\'import dans Supabase'}
            </button>
            <button
              onClick={reset}
              disabled={loading}
              className="px-6 border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 'done' && result && (
        <div>
          <div className={`rounded-xl p-4 mb-4 ${
            result.errors > 0 || result.inserted === 0
              ? 'bg-amber-50 border border-amber-200 text-amber-800'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
          }`}>
            <div className="flex items-center gap-2 font-semibold mb-2">
              {result.errors > 0 || result.inserted === 0 ? <ErrorIcon /> : <CheckIcon />}
              <span>Résultat de l'import</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <div className="text-lg font-bold">{result.totalParsed}</div>
                <div className="text-xs opacity-80">Détectés</div>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-emerald-600">{result.inserted}</div>
                <div className="text-xs opacity-80">Insérés</div>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-amber-600">{result.duplicates}</div>
                <div className="text-xs opacity-80">Doublons</div>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-red-600">{result.errors}</div>
                <div className="text-xs opacity-80">Erreurs</div>
              </div>
            </div>
            {result.details.map((d, i) => (
              <p key={i} className="text-xs mt-2 opacity-80">{d}</p>
            ))}
          </div>
          <button
            onClick={reset}
            className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <UploadIcon />
            Importer un autre fichier
          </button>
        </div>
      )}
    </div>
  );
}